<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import LoadingState from '../../../../../components/widgets/LoadingState.vue';
import { useAlert } from 'dashboard/composables';
import ChunkListItem from './components/ChunkListItem.vue';
import BatchSelectActions from './components/BatchSelectActions.vue';
import EditChunkModal from './components/EditChunkModal.vue';
import DeleteConfirmationModal from '../../../branches/components/deleteConfirmationModal.vue';
import RenameDocModal from './components/RenameDocModal.vue';

export default {
  name: 'view_document',
  components: {
    LoadingState,
    ChunkListItem,
    BatchSelectActions,
    EditChunkModal,
    DeleteConfirmationModal,
    RenameDocModal
  },
  data() {
    return {
      isLoading: true,
      isLoadingDocument: false,
      // Set when loadDocument()/loadChunks() are re-entered while already
      // in flight, so the in-flight call's own finally block re-runs the
      // load once done instead of the newer request being silently dropped.
      isDocumentReloadPending: false,
      isChunksReloadPending: false,
      documentId: null,
      document: null,
      error: null,
      documentList: [],
      isSidePanelOpen: true,
      isAvailable: true,
      isChunkEmpty: false,
      selectedChunkIds: [],
      isEditChunk: false,
      selectedChunk: null,
      isRefreshing: false,
      itemsPerPage: '10',
      statusFilter: 'all',
      filterText: '',
      isDeleting: false,
      isDeleteLoading: false,
      isChunkExpanded: false,
      isCreating: false,
      isRenaming: false,
      isShowAction: false,
      isDeletingDocument: false,
      isDeletingDocumentLoading: false,
      isMobilePanelOpen: false,
      filterDebounceTimer: null,
    };
  },
  watch:{
    documentId:{
      handler() {
        this.loadDocument();
      },
      immediate: false
    },
    filterText: {
      handler() {
        clearTimeout(this.filterDebounceTimer);
        this.filterDebounceTimer = setTimeout(() => {
          this.applyFilters();
          if (this.filteredChunks.length === 0) {
            useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.NO_CHUNKS_MATCH'));
          }
        }, 300);
      },
    },
    statusFilter() {
      this.applyFilters();
      if (this.filteredChunks.length === 0) {
        useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.NO_CHUNKS_MATCH'));
      }
    },
    itemsPerPage() {
      this.setCurrentLimit(this.itemsPerPage);
      this.loadChunks();
    }
  },
  computed: {
    ...mapState('dify', ['chunkList', 'documentDetails']),
    ...mapState('knowledge', ['knowledgeDetails']),
    ...mapGetters('dify', ['paginationInfo', 'filteredChunks']),
    datasetId(){
      return this.$route.params.id;
    },
    isLightragEngine() {
      return this.knowledgeDetails.engine === 'lightrag';
    },
    documentDateFormatted() {
      const timestamp = this.document?.data_source_detail_dict?.upload_file?.created_at;
      if (!timestamp) return '';

      const date = new Date(timestamp * 1000);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },
    // Get status text from status code
    statusText() {
      if (!this.document) return '';
      
      const statusMap = {
        'completed': 'Available',
        'indexing': 'Indexing in progress',
        'unavailable': 'Unavailable',
        'queuing': 'In queue',
        'error': 'Error processing'
      };
      
      return statusMap[this.document.indexing_status] || this.document.indexing_status;
    },
    // Get appropriate icon for document type
    fileIcon() {
      if (!this.document || !this.document.name) return 'document';
      
      const extension = this.document.name.split('.').pop().toLowerCase();
      
      if (['doc', 'docx'].includes(extension)) return 'document-word';
      if (['xls', 'xlsx'].includes(extension)) return 'document-excel';
      if (['ppt', 'pptx'].includes(extension)) return 'document-powerpoint';
      if (['pdf'].includes(extension)) return 'document-pdf';
      if (['txt'].includes(extension)) return 'document-text';
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return 'image';
      
      return 'document';
    },
    // Determine status color
    statusColor() {
      if (!this.document) return {};
      
      const status = this.document.indexing_status;
      return {
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100': status === 'completed',
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100': status === 'indexing' || status === 'queuing',
        'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100': status === 'unavailable',
        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100': status === 'error',
      };
    },
    // Determine status indicator color
    statusIndicatorColor() {
      if (!this.document) return {};
      
      const status = this.document.indexing_status;
      return {
        'bg-green-500': status === 'completed',
        'bg-yellow-500': status === 'indexing',
        'bg-slate-500': status === 'unavailable',
        'bg-yellow-300': status === 'queuing',
        'bg-red-500': status === 'error',
      };
    },
    allChunkIds() {
      return this.chunkList.map(e => e.id);
    },
    isAllSelected(){
      return this.chunkList.length > 0 && this.chunkList.length === this.selectedChunkIds.length;
    },
    currentPageDisplay() {
      return this.paginationInfo.currentPage;
    },
    totalPagesDisplay() {
      return this.paginationInfo.totalPages || 1;
    },
    hasPreviousPage() {
      return this.paginationInfo.currentPage > 1;
    },
    hasNextPage() {
      return this.paginationInfo.hasMore;
    }
  },
  methods: {
    ...mapActions('knowledge', ['fetchDocumentLists', 'handleDeleteDocument', 'fetchDatasetDetails']),
    ...mapActions('dify', ['fetchChunks', 'deleteChunk', 'toggleDocument', 'getDocumentDetails']),
    ...mapMutations('dify', [
      'setCurrentPage', 
      'setCurrentLimit',
      'setKeyword',
      'setEnabled'
    ]),
    goBackToDocuments() {
      this.$router.push({ 
        name: 'view_knowledge_documents',
        params: { id: this.datasetId }
      });
    },
    handleRename() {
      this.isRenaming = true;
      this.isShowAction = false;
    },
    toggleDeleteDocument() {
      this.isDeletingDocument = true;
      this.isShowAction = false;
    },
    formatPercentage(numerator, denominator) {
      if (denominator === 0) return '0.00%';
      return ((numerator / denominator) * 100).toFixed(2) + '%';
    },
    formatTimestamp(unix) {
      const date = new Date(unix * 1000); // Convert seconds to ms
      return date.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },
    viewDocument(document) {
      this.$store.commit('knowledge/setCurrentDocument', document);
      
      this.$router.push({ 
        name: 'view_document',
        params: { 
          documentId: document.id,
          document: document,
        }
      });

      this.documentId = document.id;
    },    
    async deleteDocument(){
      try{
        this.isDeletingDocumentLoading = true;
        await this.handleDeleteDocument({ datasetId: this.$route.params.id, document_id: this.$route.params.documentId, });
        useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.DOCUMENT_DELETED'));
        this.$router.push(`/app/accounts/${this.$store.getters.getCurrentAccountId}/knowledge/view/${this.$route.params.id}`);
      }catch(err){
        console.error(err);
      }finally{
        this.isDeletingDocumentLoading = false;
      }
    },
    // Load a (possibly new) document's chunks/details, resetting chunk
    // list state and filters first so a previously-viewed document's
    // page/filters/details don't leak into this one
    // Full load: mount and document switches only — shows the full-page
    // LoadingState. Everything else (edit/delete/rename/toggle) calls
    // refreshPage() directly, which doesn't blank the page.
    // Guarded against reentrancy: refreshPage() below assigns this.documentId
    // from the route, and that assignment itself fires the documentId
    // watcher (which also calls loadDocument()) — without this guard, the
    // very first mount always triggers two concurrent full loads racing
    // against each other, which can leave the page stuck mid-render.
    // A call arriving while one is already in flight (e.g. the document
    // selector picking a different document mid-load) isn't just dropped —
    // it's remembered and re-run once the in-flight load finishes, so the
    // view ends up on whichever document was selected last instead of
    // whichever load happened to be racing first.
    async loadDocument(){
      if (this.isLoadingDocument) {
        this.isDocumentReloadPending = true;
        return;
      }
      this.isLoadingDocument = true;
      try {
        this.$store.commit('dify/resetChunkState');
        this.filterText = '';
        this.statusFilter = 'all';
        this.isLoading = true;
        await this.refreshPage();
      } finally {
        this.isLoading = false;
        this.isLoadingDocument = false;
      }
      if (this.isDocumentReloadPending) {
        this.isDocumentReloadPending = false;
        await this.loadDocument();
      }
    },
    async refreshPage(){
      // Reset so a successful retry doesn't leave the error panel showing
      // over freshly-loaded content — only the catch block below should
      // decide whether it's shown.
      this.error = null;
      this.documentId = this.$route.params.documentId;
      this.selectedChunkIds = [];

      try {
        // Needed even though most visits arrive via Document.vue (which already
        // populates this shared state) — a direct deep-link must not skip it,
        // since engine-gating (including whether to call the Dify-only document
        // details endpoint below) depends on it. Must resolve before that call.
        await this.fetchDatasetDetails(this.datasetId);

        await this.loadChunks();

        if (!this.isLightragEngine) {
          // Dify-only endpoint — lightrag documents don't have this concept
          // and the server 404s on it.
          await this.getDocumentDetails({
            datasetId: this.$route.params.id,
            documentId: this.$route.params.documentId,
          });
          this.isAvailable = this.documentDetails.enabled;
        }

        // Fetch document list
        let currentPage = 1;
        let allDocuments = [];
        const data = {
          datasetId: this.datasetId,
          page: currentPage.toString(),
          limit: '100'
        }

        while (true) {
          const response = await this.fetchDocumentLists(data);
          allDocuments = allDocuments.concat(response.data);

          if (!response.has_more) break;
          currentPage++;
        }
        this.document = allDocuments.find((e) => e.id === this.documentId);
        this.documentList = allDocuments;
      } catch (err) {
        useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.LOAD_ERROR'));
        this.error = this.$t('KNOWLEDGE.VIEW_CHUNKS.LOAD_ERROR');
      }
    },
    async loadChunks() {
      // Same reentrancy-with-memory pattern as loadDocument() above: a call
      // arriving mid-load (e.g. a document switch firing while a filter
      // change is still fetching) is remembered rather than dropped, so the
      // chunk list ends up matching the latest documentId/filters instead
      // of whichever request was in flight first.
      if (this.isRefreshing) {
        this.isChunksReloadPending = true;
        return;
      }

      this.isRefreshing = true;
      try {
        await this.fetchChunks({
          datasetId: this.datasetId,
          documentId: this.documentId
        });

        // Update empty state based on results
        this.isChunkEmpty = this.chunkList.length === 0;
      } catch (err) {
        useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.LOAD_ERROR'));
        this.error = this.$t('KNOWLEDGE.VIEW_CHUNKS.LOAD_ERROR');
      } finally {
        this.isRefreshing = false;
      }
      if (this.isChunksReloadPending) {
        this.isChunksReloadPending = false;
        await this.loadChunks();
      }
    },
    applyFilters() {
      // Update store with current filter values
      this.setKeyword(this.filterText);
      this.setEnabled(this.statusFilter);
      this.setCurrentPage('1'); // Reset to first page when filters change
      
      // Reload chunks with new filters
      this.loadChunks();
    },
    clearFilters() {
      this.filterText = '';
      this.statusFilter = 'all';
      this.applyFilters();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPagesDisplay) return;
      
      this.setCurrentPage(page.toString());
      this.loadChunks();
    },
    goToPreviousPage() {
      if (!this.hasPreviousPage) return;
      this.goToPage(this.currentPageDisplay - 1);
    },
    goToNextPage() {
      if (!this.hasNextPage) return;
      this.goToPage(this.currentPageDisplay + 1);
    },
    toggleSelectAll(){
      if(this.isAllSelected){
        this.selectedChunkIds = [];
      }else{
        this.selectedChunkIds = this.allChunkIds;
      }
    },
    toggleDelete(id){
      this.isDeleting = true;
      this.deleteId = id;
    },
    async handleDelete(){
      try{
        this.isDeleteLoading = true;
        await this.deleteChunk({ datasetId: this.$route.params.id, documentId: this.$route.params.documentId, ids: [this.deleteId] });
        useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.CHUNK_DELETED'));
        this.cancelDelete();
        this.refreshPage();
      }catch(err){
          console.error(err);
      }finally{
        this.isDeleteLoading = false;
      }
    },
    cancelDelete(){
      this.isDeleting = false;
      this.deleteId = null;
    },
    async toggleDocumentLocal(){
      // v-model has already flipped isAvailable to the desired state by the
      // time this handler runs — capture it so we can roll back on failure.
      const desiredStatus = this.isAvailable;
      try{
        await this.toggleDocument({
          datasetId: this.$route.params.id,
          documentId: this.$route.params.documentId,
          status: desiredStatus ? 'enable' : 'disable'
        });
        useAlert(this.$t(desiredStatus ? 'KNOWLEDGE.VIEW_CHUNKS.DOCUMENT_ENABLED' : 'KNOWLEDGE.VIEW_CHUNKS.DOCUMENT_DISABLED'));
      }catch(err){
        console.error(err);
        this.isAvailable = !desiredStatus;
        useAlert(this.$t('KNOWLEDGE.VIEW_CHUNKS.TOGGLE_ERROR'));
      }
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(2)} KB`;
      const mb = kb / 1024;
      return `${mb.toFixed(2)} MB`;
    },
    formatTimeStamp(timestamp) {
      if (!timestamp) return '—';
      return new Date(timestamp).toLocaleString();
    },
    toTitleCase(text) {
      return text
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
  },
  mounted() {
    // Set initial state from data properties
    this.setCurrentLimit(this.itemsPerPage);
    this.loadDocument();
  },
}
</script>

<template>
  <div class="p-4 md:p-8 flex flex-1 h-full flex-col m-0 overflow-auto bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
    <!-- Modals -->
     <EditChunkModal v-if="selectedChunk || isCreating" :isCreating="isCreating" :read-only="isLightragEngine" @close="selectedChunk = null; isCreating = false;" :chunk="selectedChunk" @refresh="refreshPage"/>
     <RenameDocModal v-if="isRenaming" :name="document.name" @close="isRenaming = false" @refresh="refreshPage"/>
     <div
        class="fixed inset-0 flex items-center justify-center bg-slate-900/50 dark:bg-slate-800/80 h-screen w-screen z-50"
        v-if="isDeletingDocument"
      >
        <DeleteConfirmationModal
          class="z-50"
          :delete-name="document.name"
          :is-deleting="isDeletingDocumentLoading"
          @confirm-delete="deleteDocument"
          @close="isDeletingDocument = false"
        />
      </div>

    <!-- Header with back button, title, and document info -->
    <header class="mb-6">
      <!-- Back button and title -->
      <div class="flex items-center mb-2">
        <button
          @click="goBackToDocuments"
          class="mr-2 p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors duration-150 flex items-center justify-center"
          aria-label="Go back to documents list"
          data-testid="view_document_back_button"
        >
          <fluent-icon icon="chevron-left" class="h-5 w-5 text-slate-700 dark:text-slate-300" />
        </button>
        <h1 class="text-2xl font-medium text-slate-900 dark:text-slate-100 transition-colors">
          {{ $t("KNOWLEDGE.VIEW_CHUNKS.TITLE") }}
        </h1>
      </div>
      
      <!-- Description -->
      <p class="text-slate-600 dark:text-slate-400 mb-4 transition-colors">
        {{ $t("KNOWLEDGE.VIEW_CHUNKS.DESCRIPTION") }}
      </p>
    </header>

    <BatchSelectActions 
      v-if="selectedChunkIds.length > 0" 
      :selectedChunkIds="selectedChunkIds" 
      @cancelBatchSelect="selectedChunkIds = []"
      @refresh="refreshPage"
    />

    <div class="flex gap-4 justify-between">
      <!-- Document title/selection area -->
      <div>
        <select v-model="document" @change="viewDocument(document)">
          <option v-for="document in documentList" :key="document.id" :value="document">{{ document.name }}</option>
        </select>
      </div>

      <!-- Actions -->
       <div class="flex gap-4">
        <woot-button
          v-if="!isLightragEngine"
          :disabled="!isAvailable"
          icon="plus-sign"
          @click="isCreating = true"
          :title="isAvailable ?
            $t('KNOWLEDGE.VIEW_CHUNKS.ADD_CHUNK_BUTTON') : $t('KNOWLEDGE.VIEW_CHUNKS.ADD_CHUNK_BUTTON_DISABLED_TITLE')"
          data-testid="view_document_add_chunk_button"
        >
          {{ $t("KNOWLEDGE.VIEW_CHUNKS.ADD_CHUNK_BUTTON") }}</woot-button>

        <label v-if="!isLightragEngine" class="h-10 flex items-center px-2 cursor-pointer" data-testid="view_document_availability_toggle">
          <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-gray-900 dark:text-gray-100">
            <span
              class="h-2 w-2 mr-2 rounded-full"
              :class="{'bg-green-500': isAvailable, 'bg-slate-500': !isAvailable}"
            ></span>
            {{ isAvailable ? $t("KNOWLEDGE.VIEW_CHUNKS.AVAILABLE") : $t("KNOWLEDGE.VIEW_CHUNKS.DISABLED") }}
          </span>

          <div class="bg-slate-100 dark:bg-slate-700 p-2 w-[40px] h-[20px] rounded-full relative">
            <input type="checkbox" id="availability" class="peer/availability hidden" v-model="isAvailable" @change="toggleDocumentLocal" />
            <div
              class="absolute top-0 left-0 transition-all h-[20px] w-[20px] duration-500 rounded-full bg-slate-300 dark:bg-slate-200 peer-checked/availability:bg-woot-500 peer-checked/availability:translate-x-[100%]"
            ></div>
          </div>
        </label>


        <div class="relative">
          <!-- Trigger Button -->
          <woot-button 
            icon="more-vertical" 
            @click.stop="isShowAction = !isShowAction"
            aria-haspopup="true"
            :aria-expanded="isShowAction"
            aria-label="Document actions"
          ></woot-button>

          <!-- Overlay (click outside to close) -->
          <transition name="fade">
            <div 
              v-if="isShowAction" 
              class="fixed inset-0 z-40 bg-black bg-opacity-20"
              @click.self="isShowAction = false"
            ></div>
          </transition>

          <!-- Dropdown Menu -->
          <transition name="dropdown">
            <div 
              v-if="isShowAction"
              class="absolute z-50 mt-2 right-0 min-w-[180px] bg-white dark:bg-slate-700 shadow-lg rounded-md py-1"
              role="menu"
            >
              <woot-button 
                variant="clear"
                icon="edit" 
                class="w-full justify-start px-4 py-2 dark:text-slate-100"
                @click="handleRename"
                role="menuitem"
              >
                {{ $t("KNOWLEDGE.VIEW_CHUNKS.RENAME_BUTTON") }}
              </woot-button>
              
              <woot-button 
                variant="clear" 
                color-scheme="alert" 
                icon="delete" 
                class="w-full justify-start px-4 py-2 dark:text-red-400"
                @click="toggleDeleteDocument"
                role="menuitem"
              >
                {{ $t("KNOWLEDGE.DELETE_BUTTON") }}
              </woot-button>
            </div>
          </transition>
        </div>

        <div class="hidden md:block">
          <woot-button :icon="isSidePanelOpen ? 'pane-close' : 'pane-open'" @click="isSidePanelOpen = !isSidePanelOpen"></woot-button>
        </div>
        
        <woot-button class="md:hidden" :icon="isMobilePanelOpen ? 'pane-close' : 'pane-open'" @click="isMobilePanelOpen = !isMobilePanelOpen"></woot-button>
      </div>
    </div>

    <div class="flex gap-4">
      <!-- Left Column -->
      <div class="flex-1 mb-4">
        <div class="flex-1 flex justify-between gap-4 w-full">
          <div class="flex items-center gap-4">
            <input
              v-if="!isLightragEngine"
              type="checkbox"
              :checked="isAllSelected"
              @change="toggleSelectAll"
            >
            <p class="mb-0 whitespace-nowrap">{{ chunkList.length }} {{ chunkList.length > 1 ? $t("KNOWLEDGE.VIEW_CHUNKS.CHUNK_COUNT_PLURAL") : $t("KNOWLEDGE.VIEW_CHUNKS.CHUNK_COUNT")}}</p>
          </div>

          <div class="flex gap-4 items-start">  
            <select v-model="statusFilter" class="w-24 rounded-md border-gray-300 shadow-sm">
              <option value="all">{{ $t("KNOWLEDGE.VIEW_CHUNKS.ALL") }}</option>
              <option :value="true">{{ $t("KNOWLEDGE.VIEW_CHUNKS.ENABLED") }}</option>
              <option :value="false">{{ $t("KNOWLEDGE.VIEW_CHUNKS.DISABLED") }}</option>
            </select>

            <div class="relative">
              <fluent-icon 
                icon="search" 
                class="absolute top-3 left-3 text-gray-400 hover:text-gray-600"
                aria-hidden="true"
              />
              <input 
                type="text" 
                v-model="filterText" 
                :placeholder="$t('KNOWLEDGE.VIEW_CHUNKS.QUERY_PLACEHOLDER')" 
                aria-label="Search"
                class="reset-base w-full min-w-[200px] pl-10 pr-8 py-2 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-woot-500 focus:border-woot-500 outline-none transition"
              />
              <woot-button 
                v-if="filterText.length > 0" 
                @click="filterText = ''" 
                icon="dismiss" 
                aria-hidden="true"
                color-scheme="secondary"
                variant="clear" 
                size="small"
                class="absolute right-3 top-1/2 transform -translate-y-1/2"
              ></woot-button>
            </div>

            <woot-button 
              v-if="filterText.length > 0 || statusFilter !== 'all'" 
              color-scheme="secondary" 
              size="small" 
              @click="clearFilters"
            >
              {{ $t('KNOWLEDGE.VIEW_CHUNKS.CLEAR_FILTERS') }}
            </woot-button>

            <woot-button 
              color-scheme="secondary" 
              :icon="isChunkExpanded ? 'arrow-minimize' : 'arrow-maximize'" 
              v-tooltip.top="isChunkExpanded ? $t('KNOWLEDGE.VIEW_CHUNKS.COLLAPSE_CHUNKS') : $t('KNOWLEDGE.VIEW_CHUNKS.EXPAND_CHUNKS')"
              @click="isChunkExpanded = !isChunkExpanded"
            />
          </div>
        </div>
        
        <!-- Loading state -->
        <div v-if="isLoading" key="view-document-loading" class="flex-1 flex flex-col justify-center items-center py-20" data-testid="view_document_loading_state">
          <LoadingState :message="$t('KNOWLEDGE.VIEW_CHUNKS.LOADING_STATE')" />
        </div>

        <!-- Error state -->
        <div v-else-if="error" key="view-document-error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-6 rounded-lg shadow-sm" data-testid="view_document_error_state">
          <div class="flex items-center">
            <fluent-icon icon="error-circle" class="h-6 w-6 text-red-500 mr-3" />
            <h3 class="text-lg font-medium text-red-800 dark:text-red-200">{{ $t('KNOWLEDGE.VIEW_CHUNKS.ERROR') }}</h3>
          </div>
          <p class="mt-2 text-red-700 dark:text-red-300">{{ error }}</p>
          <div class="mt-4">
            <woot-button
              variant="smooth"
              color-scheme="primary"
              icon="arrow-clockwise"
              @click="refreshPage"
              data-testid="view_document_retry_button"
            >
              {{ $t('KNOWLEDGE.VIEW_CHUNKS.TRY_AGAIN') }}
            </woot-button>
          </div>
        </div>

        <!-- Chunk List -->
        <div v-else-if="!filteredChunks.length == 0" key="view-document-chunks" class="mb-4">
          <div v-if="isRefreshing || isLoading" key="view-document-chunks-refreshing" class="flex justify-center my-2">
            <LoadingState :message="$t('KNOWLEDGE.VIEW_CHUNKS.UPDATING_RESULTS')" size="small" />
          </div>

          <div
            v-else
            v-for="chunk in filteredChunks"
            class="flex gap-4 items-start my-2 transition-all duration-200"
            :key="chunk.id"
          >
            <input
              v-if="!isLightragEngine"
              type="checkbox"
              :value="chunk.id"
              v-model="selectedChunkIds"
            >
            <ChunkListItem
              v-bind="chunk"
              class="flex-1"
              @clicked="selectedChunk = chunk"
              @refresh="refreshPage"
              @delete="toggleDelete(chunk.id)"
              :isChunkExpanded="isChunkExpanded"
              :engine="isLightragEngine ? 'lightrag' : 'dify'"
            />
          </div>

          <div v-if="filteredChunks.length === 0 && !isRefreshing" class="flex justify-center my-4 py-6">
            <div class="text-center">
              <p class="text-gray-500">{{ $t('KNOWLEDGE.NO_RESULT_DESCRIPTION')}}</p>
            </div>
          </div>
        </div>

        <!-- Chunk Empty State -->
        <div v-else-if="filteredChunks.length === 0" key="view-document-chunks-empty" class="flex-1 flex justify-center items-center py-10" data-testid="view_document_chunks_empty_state">
          <div class="flex flex-col items-center text-gray-800 dark:text-gray-100">
            <span class="bg-slate-100 dark:bg-slate-800 rounded-md p-4">
              <fluent-icon icon="document" size="24" />
            </span>
            <p>{{ $t('KNOWLEDGE.VIEW_CHUNKS.NO_CHUNKS_FOUND') }}</p>
            <woot-button 
              v-if="filterText.length > 0 || statusFilter !== 'all'" 
              variant="clear" 
              @click="clearFilters"
            >
              {{ $t('KNOWLEDGE.VIEW_CHUNKS.CLEAR_FILTERS') }}
            </woot-button>
          </div>
        </div>

        <!-- Modal -->
        <div
          class="fixed inset-0 flex items-center justify-center bg-slate-900/50 dark:bg-slate-800/80 h-screen w-screen z-50"
          v-if="isDeleting"
        >
          <DeleteConfirmationModal
            :deleteName="$t('KNOWLEDGE.VIEW_CHUNKS.THIS_CHUNK')"
            :is-deleting="isDeleteLoading"
            @confirm-delete="handleDelete"
            @close="cancelDelete"
          />
        </div>


        <!-- Pagination -->
        <div class="flex flex-1 justify-between">
          <!-- Pagination Controls -->
          <div class="bg-slate-100 dark:bg-slate-800 rounded-md p-1 flex gap-2 items-center text-sm text-gray-800 dark:text-gray-100">
            <woot-button 
              icon="arrow-chevron-left" 
              size="small" 
              :disabled="!hasPreviousPage"
              @click="goToPreviousPage"
            ></woot-button>
            <p class="mb-0">{{ currentPageDisplay }}/{{ totalPagesDisplay }}</p>
            <woot-button 
              icon="arrow-chevron-right" 
              size="small" 
              :disabled="!hasNextPage"
              @click="goToNextPage"
            ></woot-button>
          </div>

          <!-- Items Per Page -->
          <div class="flex gap-2 group">
            <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-end justify-center">
              <p class="text-xs mb-0 text-gray-500 dark:text-gray-400">{{ $t('KNOWLEDGE.VIEW_CHUNKS.ITEMS') }}</p>
              <p class="text-xs mb-0 text-gray-500 dark:text-gray-400">{{ $t('KNOWLEDGE.VIEW_CHUNKS.PER_PAGE') }}</p>
            </div>
            <div class="bg-slate-100 dark:bg-slate-800 p-1 flex gap-2 text-sm rounded-md text-gray-800 dark:text-gray-100">
              <button 
                @click="itemsPerPage = '10'" 
                class="rounded-md px-2" 
                :class="{'bg-white dark:bg-slate-700 shadow': itemsPerPage === '10'}"
              >10</button>
              <button 
                @click="itemsPerPage = '15'" 
                class="rounded-md px-2" 
                :class="{'bg-white dark:bg-slate-700 shadow': itemsPerPage === '15'}"
              >15</button>
              <button 
                @click="itemsPerPage = '20'" 
                class="rounded-md px-2" 
                :class="{'bg-white dark:bg-slate-700 shadow': itemsPerPage === '20'}"
              >20</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="hidden md:block w-[388px] space-y-4 min-w-0" v-if="isSidePanelOpen && !isLoading && !error">
        <div
          v-if="isLightragEngine"
          class="w-full p-4 rounded shadow bg-white dark:bg-slate-800 dark:text-slate-100 text-xs space-y-1.5"
        >
          <h2 class="font-bold text-sm mb-2 truncate">
            {{ $t('KNOWLEDGE.VIEW_CHUNKS.KB_DETAILS_TITLE') }}
          </h2>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_ID_LABEL')
            }}</span>
            <span class="break-all text-slate-500 dark:text-slate-400">{{
              knowledgeDetails.id
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_DESCRIPTION_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              knowledgeDetails.description || '—'
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_CREATED_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              formatTimeStamp(knowledgeDetails.created_at)
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_UPDATED_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              formatTimeStamp(knowledgeDetails.updated_at)
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_GRAPH_ENABLED_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              knowledgeDetails.graph_enabled
                ? $t('KNOWLEDGE.DETAILS_VALUE_ON')
                : $t('KNOWLEDGE.DETAILS_VALUE_OFF')
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_IMAGES_ENABLED_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              knowledgeDetails.images_enabled
                ? $t('KNOWLEDGE.DETAILS_VALUE_ON')
                : $t('KNOWLEDGE.DETAILS_VALUE_OFF')
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_CHUNK_SIZE_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              knowledgeDetails.chunk_word_size ||
              $t('KNOWLEDGE.DETAILS_VALUE_DEFAULT')
            }}</span>
          </p>
          <p>
            <span class="font-medium text-slate-600 dark:text-slate-300">{{
              $t('KNOWLEDGE.DETAILS_CHUNK_OVERLAP_LABEL')
            }}</span>
            <span class="text-slate-500 dark:text-slate-400">{{
              knowledgeDetails.chunk_overlap_word_size ||
              $t('KNOWLEDGE.DETAILS_VALUE_DEFAULT')
            }}</span>
          </p>
          <template v-if="knowledgeDetails.breakdown">
            <hr class="border-slate-100 dark:border-slate-700" />
            <p class="font-medium text-slate-600 dark:text-slate-300">
              {{ $t('KNOWLEDGE.DETAILS_BREAKDOWN_TITLE') }}
            </p>
            <p>
              <span class="font-medium text-slate-600 dark:text-slate-300">{{
                $t('KNOWLEDGE.DETAILS_BREAKDOWN_DOCUMENTS_LABEL')
              }}</span>
              <span class="text-slate-500 dark:text-slate-400">{{
                knowledgeDetails.breakdown.documents
              }}</span>
            </p>
            <p>
              <span class="font-medium text-slate-600 dark:text-slate-300">{{
                $t('KNOWLEDGE.DETAILS_BREAKDOWN_WORDS_LABEL')
              }}</span>
              <span class="text-slate-500 dark:text-slate-400">{{
                knowledgeDetails.breakdown.words
              }}</span>
            </p>
            <p>
              <span class="font-medium text-slate-600 dark:text-slate-300">{{
                $t('KNOWLEDGE.DETAILS_BREAKDOWN_WORD_CREDITS_LABEL')
              }}</span>
              <span class="text-slate-500 dark:text-slate-400">{{
                knowledgeDetails.breakdown.word_credits
              }}</span>
            </p>
            <p>
              <span class="font-medium text-slate-600 dark:text-slate-300">{{
                $t('KNOWLEDGE.DETAILS_BREAKDOWN_GRAPH_CREDITS_LABEL')
              }}</span>
              <span class="text-slate-500 dark:text-slate-400">{{
                knowledgeDetails.breakdown.graph_credits
              }}</span>
            </p>
            <p>
              <span class="font-medium text-slate-600 dark:text-slate-300">{{
                $t('KNOWLEDGE.DETAILS_BREAKDOWN_IMAGE_CREDITS_LABEL')
              }}</span>
              <span class="text-slate-500 dark:text-slate-400">{{
                knowledgeDetails.breakdown.image_credits
              }}</span>
            </p>
            <p>
              <span class="font-medium text-slate-600 dark:text-slate-300">{{
                $t('KNOWLEDGE.DETAILS_BREAKDOWN_TOTAL_CREDITS_LABEL')
              }}</span>
              <span class="font-medium text-slate-700 dark:text-slate-200">{{
                knowledgeDetails.breakdown.total_credits
              }}</span>
            </p>
          </template>
        </div>
        <!-- Document information -->
        <div
          v-else
          class="w-full p-4 rounded shadow bg-white dark:bg-slate-800 dark:text-slate-100"
        >
          <h2 class="font-bold text-sm mb-4 truncate">{{ $t('KNOWLEDGE.VIEW_CHUNKS.INFORMATION') }}</h2>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.ORIGINAL_FILENAME') }}:</strong>
            {{ documentDetails.data_source_info.upload_file.name }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.ORIGINAL_FILE_SIZE') }}:</strong>
            {{ formatFileSize(documentDetails.data_source_info.upload_file.size) }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.UPLOAD_DATE') }}:</strong>
            <!-- {{ formatTimestamp(documentDetails.created_at) }} -->
            {{ documentDetails.created_at_formatted }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.LAST_UPDATE_DATE') }}:</strong>
            <!-- {{ formatTimestamp(documentDetails.updated_at) }} -->
              {{ documentDetails.updated_at_formatted }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.SOURCE') }}:</strong>
            {{ toTitleCase(documentDetails.data_source_type) }}
          </div>
        </div>

        <!-- Technical Parameters -->
        <div
          v-if="!isLightragEngine"
          class="w-full p-4 rounded shadow bg-white dark:bg-slate-800 dark:text-slate-100 mt-6"
        >
          <h2 class="font-bold text-sm mb-4 truncate">{{ $t('KNOWLEDGE.VIEW_CHUNKS.TECHNICAL_PARAMETERS') }}</h2>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.CHUNKS_LENGTH') }}:</strong>
            {{ documentDetails.dataset_process_rule.rules.segmentation.max_tokens }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.AVG_PARAGRAPH_LENGTH') }}:</strong>
            {{ documentDetails.average_segment_length }}
            {{ documentDetails.average_segment_length > 1 ? $t('KNOWLEDGE.VIEW_CHUNKS.CHARACTERS') : $t('KNOWLEDGE.VIEW_CHUNKS.CHARACTER') }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.PARAGRAPHS') }}:</strong>
            {{ documentDetails.segment_count }}
            {{ documentDetails.segment_count > 1 ? $t('KNOWLEDGE.VIEW_CHUNKS.PARAGRAPHS') : $t('KNOWLEDGE.VIEW_CHUNKS.PARAGRAPH') }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.RETRIEVAL_COUNT') }}:</strong>
            {{ documentDetails.hit_count }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.EMBEDDING_TIME') }}:</strong>
            {{ Number(documentDetails.indexing_latency.toFixed(2)) }} {{ $t('KNOWLEDGE.VIEW_CHUNKS.SEC') }}
          </div>

          <div class="text-xs truncate mb-2">
            <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.EMBEDDED_SPEND') }}:</strong>
            {{ documentDetails.tokens }}
            {{ documentDetails.tokens > 1 ? $t('KNOWLEDGE.VIEW_CHUNKS.TOKENS') : $t('KNOWLEDGE.VIEW_CHUNKS.TOKEN') }}
          </div>
        </div>
      </div>


      <!-- Mobile Modal (shown on smaller screens) -->
      <div class="md:hidden" v-if="isMobilePanelOpen && !isLoading && !error">
        <!-- Modal Overlay -->
        <transition name="fade">
          <div 
            v-if="isMobilePanelOpen"
            class="fixed inset-0 bg-slate-800/50 z-40"
            @click="isMobilePanelOpen = false"
          ></div>
        </transition>

        <!-- Modal Content -->
        <transition name="slide-up">
          <div 
            v-if="isMobilePanelOpen"
            class="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-800 dark:text-slate-100 rounded-t-lg p-4 max-h-[80vh] overflow-y-auto"
          >
            <!-- Modal Header -->
            <div class="flex justify-between items-center mb-4">
              <h2 class="font-bold text-lg">Document Details</h2>
              <button @click="isMobilePanelOpen = false">
                <svg class="w-6 h-6 text-slate-800 dark:text-slate-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <!-- Modal Body -->
            <div
              v-if="isLightragEngine"
              class="w-full p-4 rounded shadow bg-white dark:bg-slate-700 dark:text-slate-100 text-xs space-y-1.5"
            >
              <h2 class="font-bold text-sm mb-2 truncate">
                {{ $t('KNOWLEDGE.VIEW_CHUNKS.KB_DETAILS_TITLE') }}
              </h2>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_ID_LABEL')
                }}</span>
                <span class="break-all text-slate-500 dark:text-slate-400">{{
                  knowledgeDetails.id
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_DESCRIPTION_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  knowledgeDetails.description || '—'
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_CREATED_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  formatTimeStamp(knowledgeDetails.created_at)
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_UPDATED_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  formatTimeStamp(knowledgeDetails.updated_at)
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_GRAPH_ENABLED_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  knowledgeDetails.graph_enabled
                    ? $t('KNOWLEDGE.DETAILS_VALUE_ON')
                    : $t('KNOWLEDGE.DETAILS_VALUE_OFF')
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_IMAGES_ENABLED_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  knowledgeDetails.images_enabled
                    ? $t('KNOWLEDGE.DETAILS_VALUE_ON')
                    : $t('KNOWLEDGE.DETAILS_VALUE_OFF')
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_CHUNK_SIZE_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  knowledgeDetails.chunk_word_size ||
                  $t('KNOWLEDGE.DETAILS_VALUE_DEFAULT')
                }}</span>
              </p>
              <p>
                <span class="font-medium text-slate-600 dark:text-slate-300">{{
                  $t('KNOWLEDGE.DETAILS_CHUNK_OVERLAP_LABEL')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">{{
                  knowledgeDetails.chunk_overlap_word_size ||
                  $t('KNOWLEDGE.DETAILS_VALUE_DEFAULT')
                }}</span>
              </p>
              <template v-if="knowledgeDetails.breakdown">
                <hr class="border-slate-100 dark:border-slate-700" />
                <p class="font-medium text-slate-600 dark:text-slate-300">
                  {{ $t('KNOWLEDGE.DETAILS_BREAKDOWN_TITLE') }}
                </p>
                <p>
                  <span
                    class="font-medium text-slate-600 dark:text-slate-300"
                    >{{
                      $t('KNOWLEDGE.DETAILS_BREAKDOWN_DOCUMENTS_LABEL')
                    }}</span>
                  <span class="text-slate-500 dark:text-slate-400">{{
                    knowledgeDetails.breakdown.documents
                  }}</span>
                </p>
                <p>
                  <span
                    class="font-medium text-slate-600 dark:text-slate-300"
                    >{{ $t('KNOWLEDGE.DETAILS_BREAKDOWN_WORDS_LABEL') }}</span>
                  <span class="text-slate-500 dark:text-slate-400">{{
                    knowledgeDetails.breakdown.words
                  }}</span>
                </p>
                <p>
                  <span
                    class="font-medium text-slate-600 dark:text-slate-300"
                    >{{
                      $t('KNOWLEDGE.DETAILS_BREAKDOWN_WORD_CREDITS_LABEL')
                    }}</span>
                  <span class="text-slate-500 dark:text-slate-400">{{
                    knowledgeDetails.breakdown.word_credits
                  }}</span>
                </p>
                <p>
                  <span
                    class="font-medium text-slate-600 dark:text-slate-300"
                    >{{
                      $t('KNOWLEDGE.DETAILS_BREAKDOWN_GRAPH_CREDITS_LABEL')
                    }}</span>
                  <span class="text-slate-500 dark:text-slate-400">{{
                    knowledgeDetails.breakdown.graph_credits
                  }}</span>
                </p>
                <p>
                  <span
                    class="font-medium text-slate-600 dark:text-slate-300"
                    >{{
                      $t('KNOWLEDGE.DETAILS_BREAKDOWN_IMAGE_CREDITS_LABEL')
                    }}</span>
                  <span class="text-slate-500 dark:text-slate-400">{{
                    knowledgeDetails.breakdown.image_credits
                  }}</span>
                </p>
                <p>
                  <span
                    class="font-medium text-slate-600 dark:text-slate-300"
                    >{{
                      $t('KNOWLEDGE.DETAILS_BREAKDOWN_TOTAL_CREDITS_LABEL')
                    }}</span>
                  <span
                    class="font-medium text-slate-700 dark:text-slate-200"
                    >{{ knowledgeDetails.breakdown.total_credits }}</span>
                </p>
              </template>
            </div>
            <div v-else class="space-y-4">
              <!-- Document information -->
              <div class="w-full p-4 rounded shadow bg-white dark:bg-slate-700">
                <h2 class="font-bold text-sm mb-4 truncate">{{ $t('KNOWLEDGE.VIEW_CHUNKS.INFORMATION') }}</h2>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.ORIGINAL_FILENAME') }}:</strong>
                  {{ documentDetails.data_source_info.upload_file.name }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.ORIGINAL_FILE_SIZE') }}:</strong>
                  {{ formatFileSize(documentDetails.data_source_info.upload_file.size) }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.UPLOAD_DATE') }}:</strong>
                  <!-- {{ formatTimestamp(documentDetails.created_at) }} -->
                  {{ documentDetails.created_at_formatted }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.LAST_UPDATE_DATE') }}:</strong>
                  <!-- {{ formatTimestamp(documentDetails.updated_at) }} -->
                    {{ documentDetails.updated_at_formatted }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.SOURCE') }}:</strong>
                  {{ toTitleCase(documentDetails.data_source_type) }}
                </div>
              </div>

              <!-- Technical Parameters -->
              <div class="w-full p-4 rounded shadow bg-white dark:bg-slate-700">
                <h2 class="font-bold text-sm mb-4 truncate">{{ $t('KNOWLEDGE.VIEW_CHUNKS.TECHNICAL_PARAMETERS') }}</h2>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.CHUNKS_LENGTH') }}:</strong>
                  {{ documentDetails.dataset_process_rule.rules.segmentation.max_tokens }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.AVG_PARAGRAPH_LENGTH') }}:</strong>
                  {{ documentDetails.average_segment_length }}
                  {{ documentDetails.average_segment_length > 1 ? $t('KNOWLEDGE.VIEW_CHUNKS.CHARACTERS') : $t('KNOWLEDGE.VIEW_CHUNKS.CHARACTER') }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.PARAGRAPHS') }}:</strong>
                  {{ documentDetails.segment_count }}
                  {{ documentDetails.segment_count > 1 ? $t('KNOWLEDGE.VIEW_CHUNKS.PARAGRAPHS') : $t('KNOWLEDGE.VIEW_CHUNKS.PARAGRAPH') }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.RETRIEVAL_COUNT') }}:</strong>
                  {{ documentDetails.hit_count }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.EMBEDDING_TIME') }}:</strong>
                  {{ Number(documentDetails.indexing_latency.toFixed(2)) }} {{ $t('KNOWLEDGE.VIEW_CHUNKS.SEC') }}
                </div>

                <div class="text-xs truncate mb-2">
                  <strong>{{ $t('KNOWLEDGE.VIEW_CHUNKS.EMBEDDED_SPEND') }}:</strong>
                  {{ documentDetails.tokens }}
                  {{ documentDetails.tokens > 1 ? $t('KNOWLEDGE.VIEW_CHUNKS.TOKENS') : $t('KNOWLEDGE.VIEW_CHUNKS.TOKEN') }}
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loading-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.2s ease;
  transform-origin: top right;
}
.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
}
</style>